import React, { useCallback, useEffect, useRef, useState } from "react";
import { ClubService } from "../../../../services/clubService";
import { normalizeLinks } from "../../../../utils/clubLinks";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useToast } from "../../components/Toast";
import Icon from "../../components/Icon";
import { Button, ClubLogo, Field, IconButton, Pill } from "../../components/primitives";
import ConfirmModal from "../../components/manage/ConfirmModal";
import GalleryEditor from "../../components/manage/GalleryEditor";
import LinkModal from "../../components/manage/LinkModal";
import { compressForAvatar, isLocalUri, pdfToAsset, pickFiles, releaseUri } from "../../lib/files";
import { usePendingWork } from "../../lib/pendingWork";
import { saveBio } from "./saveBio";
import { msg } from "./manageUtils";

const seedForm = (club) => ({
  name: club?.name || "",
  description: club?.description || "",
  links: normalizeLinks(club?.links),
  pdfs: Array.isArray(club?.pdfs) ? club.pdfs : [],
  gallery: Array.isArray(club?.gallery) ? club.gallery.filter((u) => typeof u === "string" && u.length > 0) : [],
  logo: club?.logo_url || null,
  category: club?.category || "",
  category_id: club?.category_id || null,
});

/** Edit Club Bio (AdminScreen :1629-1923 + components/admin/EditBioSheet.js), as a page tab. */
export default function EditBioTab({ club, photos, keepStaged, onPatch, onClose, onSavingChange, onDirtyChange }) {
  const { user } = useAuth();
  const { loadUserClubs } = useData();
  const toast = useToast();
  const [form, setForm] = useState(() => seedForm(club));
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const lockedRef = useRef(false);
  const [logoBusy, setLogoBusy] = useState(false); // compressing a picked logo: Save must wait for it
  const [error, setError] = useState(null);
  const [linkModal, setLinkModal] = useState(null); // { index: number|null }
  const [confirmLogo, setConfirmLogo] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const dirty = useRef(false);
  const [isDirty, setIsDirty] = useState(false); // render copy of `dirty` for the unsaved-changes guards
  const galleryRef = useRef(null);

  // Re-hydrate from the (patched / revalidated) club unless the admin has unsaved edits.
  useEffect(() => { if (!dirty.current) setForm(seedForm(club)); }, [club]);

  const edit = useCallback((fn) => { dirty.current = true; setIsDirty(true); setForm(fn); }, []);

  // Unsaved = edited fields or staged gallery photos. ClubManager guards tab / club / in-app link changes
  // with it; the browser's own prompt covers reload and closing the tab.
  const unsaved = isDirty || photos.staged.length > 0;
  const dirtyCbRef = useRef(onDirtyChange);
  dirtyCbRef.current = onDirtyChange;
  useEffect(() => { dirtyCbRef.current?.(unsaved); }, [unsaved]);
  useEffect(() => () => dirtyCbRef.current?.(false), []);
  useEffect(() => {
    if (!unsaved) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  // A normal open starts a clean photo session; the Overview "Add Photos" tile keeps its staged photos.
  useEffect(() => {
    if (!keepStaged) photos.cancel();
    else galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let live = true;
    ClubService.getCategories().then(({ data, error: err }) => {
      if (live && !err && Array.isArray(data)) setCategories(data);
    });
    return () => { live = false; };
  }, []);

  const pickLogo = async () => {
    const [file] = await pickFiles({ accept: "image/*" });
    if (!file) return;
    setLogoBusy(true);
    try {
      const uri = await compressForAvatar(file);
      edit((p) => { if (isLocalUri(p.logo)) releaseUri(p.logo); return { ...p, logo: uri }; });
    } catch (e) {
      toast(msg(e, "Failed to pick image"), "alert");
    } finally {
      setLogoBusy(false);
    }
  };

  const addPdf = async () => {
    const [file] = await pickFiles({ accept: "application/pdf,.pdf" });
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") { toast("Choose a PDF file.", "alert"); return; }
    edit((p) => ({ ...p, pdfs: [...p.pdfs, pdfToAsset(file)] }));
  };

  const onPickPhotos = async () => { await photos.pickPhotos(form.gallery); };
  // Locked like the fieldset below: saveBio persists a snapshot and then replaces the form with it.
  const onDropPhotos = (files) => { if (!lockedRef.current) photos.addPhotos(files, form.gallery); };

  const submitLink = (link) => {
    edit((p) => ({
      ...p,
      links: linkModal.index == null ? [...p.links, link] : p.links.map((l, i) => (i === linkModal.index ? link : l)),
    }));
    setLinkModal(null);
  };

  const onSave = async () => {
    setSaving(true);
    onSavingChange?.(true);
    photos.savingRef.current = true;
    setError(null);
    try {
      const res = await saveBio({
        form, clubId: club.id, uid: user?.uid, photos, loadUserClubs, onPatch, notify: toast,
      });
      if (res.error) { setError(res.error); return; }
      dirty.current = false;
      setIsDirty(false);
      // Re-seed from what was saved: staged tiles are gone after reset(), so the form must hold the real gallery.
      const next = seedForm({ ...club, ...res.saved });
      if (isLocalUri(form.logo)) next.logo = form.logo; // new logo still attaching in the background
      setForm(next);
      toast("Club bio updated successfully!");
    } catch (e) {
      setError(msg(e, "Failed to update club bio. Please try again."));
    } finally {
      photos.savingRef.current = false;
      onSavingChange?.(false);
      setSaving(false);
    }
  };

  const discard = async () => {
    dirty.current = false;
    setIsDirty(false);
    setConfirmDiscard(false);
    await photos.cancel();
    onClose();
  };
  const onCancel = () => { if (unsaved) setConfirmDiscard(true); else discard(); };

  const attaching = usePendingWork().has(`club:${club.id}`);
  const busy = saving || photos.uploading || logoBusy;
  lockedRef.current = busy || attaching;
  const catValue = form.category_id || "";
  const catKnown = categories.some((c) => c.id === form.category_id);
  // A legacy club has only a raw `category` string ("general") and no category_id: show a placeholder, not the raw value.
  const catPlaceholder = form.category_id ? form.category || "Select a category" : "Select a category";

  return (
    <div className="card pad col" style={{ gap: 16 }}>
      <fieldset className="col" disabled={attaching || busy} style={{ gap: 16, border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div className="row">
        <ClubLogo club={{ ...club, logo_url: form.logo }} size={76} />
        <Button variant="sec" size="sm" icon="upload" onClick={pickLogo} disabled={busy} data-testid="manage.bio.logo">Change logo</Button>
        {form.logo && <Button variant="ghost" size="sm" onClick={() => setConfirmLogo(true)} disabled={busy} data-testid="manage.bio.removeLogo">Remove</Button>}
      </div>

      <div className="grid g2">
        <Field label="Club name *" value={form.name} onChange={(e) => edit((p) => ({ ...p, name: e.target.value }))} placeholder="Enter club name" data-testid="manage.bio.name" />
        <Field
          label="Category"
          as="select"
          data-testid="manage.bio.category"
          value={catValue}
          disabled={busy}
          onChange={(e) => {
            const opt = categories.find((c) => c.id === e.target.value);
            if (opt) edit((p) => ({ ...p, category: opt.name, category_id: opt.id }));
          }}
        >
          {!catKnown && <option value={catValue} disabled={!catValue}>{catPlaceholder}</option>}
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Field>
      </div>

      <Field
        label="Description *"
        as="textarea"
        rows={5}
        value={form.description}
        onChange={(e) => edit((p) => ({ ...p, description: e.target.value }))}
        placeholder="Enter club description"
        data-testid="manage.bio.description"
      />

      <div className="field">
        <span>Platforms</span>
        {form.links.map((l, i) => (
          <div key={i} className="mg-subrow">
            <Icon name="link" size={16} />
            <b>{l.name || l.url}</b>
            <span className="meta grow ellip">{l.name ? l.url : ""}</span>
            {l.is_members_only && <Pill tone="purple">Members only</Pill>}
            <IconButton icon="edit" label="Edit link" size={15} onClick={() => setLinkModal({ index: i })} disabled={busy} data-testid={`manage.bio.link.${i}.edit`} />
            <IconButton icon="x" label="Remove link" size={15} onClick={() => edit((p) => ({ ...p, links: p.links.filter((_, j) => j !== i) }))} data-testid={`manage.bio.link.${i}.remove`} />
          </div>
        ))}
        <Button variant="ghost" size="sm" icon="plus" style={{ alignSelf: "flex-start" }} onClick={() => setLinkModal({ index: null })} disabled={busy} data-testid="manage.bio.addLink">Add link</Button>
      </div>

      <div className="field">
        <span>Documents</span>
        {form.pdfs.map((d, i) => (
          <div key={i} className="mg-subrow">
            <Icon name="file" size={16} />
            <b className="grow ellip">{typeof d === "string" ? `Document ${i + 1}` : d.name || `Document ${i + 1}`}</b>
            <IconButton icon="x" label="Remove document" size={15} onClick={() => edit((p) => ({ ...p, pdfs: p.pdfs.filter((_, j) => j !== i) }))} data-testid={`manage.bio.document.${i}.remove`} />
          </div>
        ))}
        <Button variant="ghost" size="sm" icon="upload" style={{ alignSelf: "flex-start" }} onClick={addPdf} disabled={busy} data-testid="manage.bio.uploadPdf">Upload PDF</Button>
      </div>

      <GalleryEditor
        ref={galleryRef}
        saved={form.gallery}
        staged={photos.staged}
        uploading={photos.uploading}
        onRemoveSaved={(i) => edit((p) => ({ ...p, gallery: p.gallery.filter((_, j) => j !== i) }))}
        onRemoveStaged={photos.remove}
        onPick={onPickPhotos}
        onDropFiles={onDropPhotos}
      />
      </fieldset>

      {attaching && <div className="meta" role="status">Finishing uploads…</div>}
      {error && <div className="err-text" role="alert">{error}</div>}
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <Button variant="ghost" onClick={onCancel} disabled={saving || photos.uploading} data-testid="manage.bio.cancel">Cancel</Button>
        <Button onClick={onSave} disabled={busy || attaching} data-testid="manage.bio.save">{logoBusy ? "Processing logo…" : busy ? "Saving..." : attaching ? "Finishing uploads…" : "Save changes"}</Button>
      </div>

      {linkModal && (
        <LinkModal
          initial={linkModal.index == null ? null : form.links[linkModal.index]}
          onSubmit={submitLink}
          onClose={() => setLinkModal(null)}
          testId="manage.bio.linkModal"
        />
      )}
      {confirmLogo && (
        <ConfirmModal
          title="Remove logo"
          confirmLabel="Remove"
          danger
          testId="manage.bio.removeLogoConfirm"
          onClose={() => setConfirmLogo(false)}
          onConfirm={() => { edit((p) => ({ ...p, logo: null })); setConfirmLogo(false); }}
        >
          Are you sure you want to remove the club logo?
        </ConfirmModal>
      )}
      {confirmDiscard && (
        <ConfirmModal
          title="Discard changes?"
          confirmLabel="Discard"
          danger
          testId="manage.bio.discardConfirm"
          onClose={() => setConfirmDiscard(false)}
          onConfirm={discard}
        >
          Your unsaved changes to this club&apos;s bio will be lost.
        </ConfirmModal>
      )}
    </div>
  );
}
